import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Button, Card } from '../components/common';
import PresetForm from '../components/preset/PresetForm';
import { presetService } from '../services/presetService';
import { Preset, PresetUpdateRequest } from '../types/preset';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PageTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const HeaderButtons = styled.div`
  display: flex;
  gap: 1rem;
`;

const PresetDetailContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const DetailSection = styled.div`
  margin-bottom: 1rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 600;
  color: #4b5563;
  margin: 0 0 0.75rem 0;
`;

const DetailRow = styled.div`
  display: flex;
  margin-bottom: 0.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailLabel = styled.span`
  font-weight: 500;
  color: #6b7280;
  width: 150px;
`;

const DetailValue = styled.span`
  color: #1f2937;
`;

const LoadingMessage = styled.p`
  color: #6b7280;
  font-style: italic;
`;

const ErrorMessage = styled.div`
  color: #dc2626;
  padding: 1rem;
  background-color: #fee2e2;
  border-radius: 0.375rem;
  margin-bottom: 1rem;
`;

const PresetDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [preset, setPreset] = useState<Preset | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionMessage, setExecutionMessage] = useState<string | null>(null);
  
  useEffect(() => {
    fetchPreset();
  }, [id]);
  
  const fetchPreset = async () => {
    if (!id) return;
    
    try {
      setIsLoading(true);
      setError(null);
      const data = await presetService.getPresetById(id);
      setPreset(data);
    } catch (err) {
      console.error('Error fetching preset:', err);
      setError('Failed to load preset details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleEdit = () => {
    setIsEditing(true);
  };
  
  const handleCancel = () => {
    setIsEditing(false);
  };
  
  const handleUpdate = async (formData: PresetUpdateRequest) => {
    if (!id) return;
    
    try {
      setIsSaving(true);
      setError(null);
      const updatedPreset = await presetService.updatePreset(id, formData);
      setPreset(updatedPreset);
      setIsEditing(false);
    } catch (err) {
      console.error('Error updating preset:', err);
      setError('Failed to update preset. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleDelete = async () => {
    if (!id || !window.confirm('Are you sure you want to delete this preset? This action cannot be undone.')) {
      return;
    }
    
    try {
      setIsDeleting(true);
      setError(null);
      await presetService.deletePreset(id);
      navigate('/presets');
    } catch (err) {
      console.error('Error deleting preset:', err);
      setError('Failed to delete preset. Please try again.');
      setIsDeleting(false);
    }
  };
  
  const handleBack = () => {
    navigate('/presets');
  };
  
  const handleExecute = async () => {
    if (!id) return;
    
    try {
      setIsExecuting(true);
      setError(null);
      setExecutionMessage(null);
      
      const result = await presetService.executePreset(id);
      
      setExecutionMessage(result.message || 'Preset executed successfully.');
    } catch (err) {
      console.error('Error executing preset:', err);
      setError('Failed to execute preset. Please try again.');
    } finally {
      setIsExecuting(false);
    }
  };
  
  const renderPresetDetails = () => {
    if (!preset) return null;
    
    return (
      <Card>
        {executionMessage && (
          <div style={{ 
            padding: '0.75rem', 
            marginBottom: '1rem', 
            backgroundColor: '#f0fdf4', 
            border: '1px solid #86efac',
            borderRadius: '0.375rem',
            color: '#166534'
          }}>
            {executionMessage}
          </div>
        )}
        <DetailSection>
          <SectionTitle>Basic Information</SectionTitle>
          <DetailRow>
            <DetailLabel>Name:</DetailLabel>
            <DetailValue>{preset.name}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>Description:</DetailLabel>
            <DetailValue>{preset.description}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>Created:</DetailLabel>
            <DetailValue>{new Date(preset.created_at).toLocaleString()}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>Last Updated:</DetailLabel>
            <DetailValue>{new Date(preset.updated_at).toLocaleString()}</DetailValue>
          </DetailRow>
        </DetailSection>
        
        <DetailSection>
          <SectionTitle>Order Parameters</SectionTitle>
          <DetailRow>
            <DetailLabel>Symbol:</DetailLabel>
            <DetailValue>{preset.symbol}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>Order Type:</DetailLabel>
            <DetailValue>{preset.order_type}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>Side:</DetailLabel>
            <DetailValue>{preset.side}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>Amount:</DetailLabel>
            <DetailValue>{preset.amount} ({preset.amount_type})</DetailValue>
          </DetailRow>
          {preset.limit_price && (
            <DetailRow>
              <DetailLabel>Limit Price:</DetailLabel>
              <DetailValue>{preset.limit_price} ({preset.limit_price_type || 'absolute'})</DetailValue>
            </DetailRow>
          )}
          {preset.stop_price && (
            <DetailRow>
              <DetailLabel>Stop Price:</DetailLabel>
              <DetailValue>{preset.stop_price}</DetailValue>
            </DetailRow>
          )}
        </DetailSection>

        <DetailSection>
          <SectionTitle>Risk Management</SectionTitle>
          <DetailRow>
            <DetailLabel>Leverage:</DetailLabel>
            <DetailValue>{preset.leverage}x</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>Margin Mode:</DetailLabel>
            <DetailValue>{preset.margin_mode}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>Take Profit:</DetailLabel>
            <DetailValue>{preset.take_profit?.enabled ? 'Enabled' : 'Disabled'}</DetailValue>
          </DetailRow>
          {preset.take_profit?.enabled && (
            <>
              <DetailRow style={{ paddingLeft: '1rem' }}>
                <DetailLabel>TP Trigger Price:</DetailLabel>
                <DetailValue>{preset.take_profit.trigger_price_value} ({preset.take_profit.price_type || 'absolute'})</DetailValue>
              </DetailRow>
              {preset.take_profit.limit_price_value && (
                <DetailRow style={{ paddingLeft: '1rem' }}>
                  <DetailLabel>TP Limit Price:</DetailLabel>
                  <DetailValue>{preset.take_profit.limit_price_value} ({preset.take_profit.limit_price_type || 'absolute'})</DetailValue>
                </DetailRow>
              )}
            </>
          )}
          <DetailRow>
            <DetailLabel>Stop Loss:</DetailLabel>
            <DetailValue>{preset.stop_loss?.enabled ? 'Enabled' : 'Disabled'}</DetailValue>
          </DetailRow>
          {preset.stop_loss?.enabled && (
            <>
              <DetailRow style={{ paddingLeft: '1rem' }}>
                <DetailLabel>SL Trigger Price:</DetailLabel>
                <DetailValue>{preset.stop_loss.trigger_price_value} ({preset.stop_loss.price_type || 'absolute'})</DetailValue>
              </DetailRow>
              {preset.stop_loss.limit_price_value && (
                 <DetailRow style={{ paddingLeft: '1rem' }}>
                  <DetailLabel>SL Limit Price:</DetailLabel>
                  <DetailValue>{preset.stop_loss.limit_price_value} ({preset.stop_loss.limit_price_type || 'absolute'})</DetailValue>
                </DetailRow>
              )}
            </>
          )}
        </DetailSection>

        {preset.layered && (
          <DetailSection>
            <SectionTitle>Layered Order</SectionTitle>
             <DetailRow>
              <DetailLabel>Parts:</DetailLabel>
              <DetailValue>{preset.layered.parts}</DetailValue>
            </DetailRow>
             <DetailRow>
              <DetailLabel>Step:</DetailLabel>
              <DetailValue>{preset.layered.step}</DetailValue>
            </DetailRow>
          </DetailSection>
        )}

        <DetailSection>
           <SectionTitle>Flags</SectionTitle>
           <DetailRow>
            <DetailLabel>Reduce Only:</DetailLabel>
            <DetailValue>{preset.flags?.reduce_only ? 'Yes' : 'No'}</DetailValue>
          </DetailRow>
           <DetailRow>
            <DetailLabel>Post Only:</DetailLabel>
            <DetailValue>{preset.flags?.post_only ? 'Yes' : 'No'}</DetailValue>
          </DetailRow>
        </DetailSection>
        
        <DetailSection>
          <SectionTitle>Webhook</SectionTitle>
          <DetailRow>
            <DetailLabel>Enabled:</DetailLabel>
            <DetailValue>{preset.webhook_enabled ? 'Yes' : 'No'}</DetailValue>
          </DetailRow>
          {preset.webhook_enabled && (
             <DetailRow>
              <DetailLabel>Webhook ID:</DetailLabel>
              <DetailValue>{preset.webhook_id || 'Not Set'}</DetailValue>
            </DetailRow>
          )}
        </DetailSection>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1rem' }}>
          <Button variant="secondary" onClick={handleBack}>
            Back to Presets
          </Button>
          <Button 
            variant="primary" 
            onClick={handleExecute}
            disabled={isExecuting}
          >
            {isExecuting ? 'Executing...' : 'Execute Preset'}
          </Button>
          <Button onClick={() => navigate(`/admin/presets/${id}/edit`)}>
            Edit Preset
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete Preset'}
          </Button>
        </div>
      </Card>
    );
  };
  
  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>
          {isEditing ? 'Edit Preset' : 'Preset Details'}
        </PageTitle>
        {!isEditing && (
          <HeaderButtons>
            <Button variant="secondary" onClick={handleBack}>
              Back
            </Button>
          </HeaderButtons>
        )}
      </PageHeader>
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
      
      <PresetDetailContainer>
        {isLoading ? (
          <LoadingMessage>Loading preset details...</LoadingMessage>
        ) : isEditing ? (
          <PresetForm 
            preset={preset || undefined}
            onSubmit={handleUpdate}
            onCancel={handleCancel}
            isLoading={isSaving}
          />
        ) : (
          renderPresetDetails()
        )}
      </PresetDetailContainer>
    </PageContainer>
  );
};

export default PresetDetailPage; 